'use client'

// session/session-store.ts — zero-dependency external store backing the session
// state, enabling selector-level subscriptions via useSyncExternalStore (HAB-142).
//
// Why this exists:
//   Plain React Context re-renders *every* consumer of a context whenever the
//   context value changes — there is no built-in selector granularity. HAB-49
//   split Session into focused slice contexts, but two problems remained:
//     1. A slice context still re-renders all of its consumers when any field in
//        that slice changes (e.g. a notifications-only consumer re-renders when
//        the theme changes).
//     2. `normalizeSessionState` (see normalizers.ts) rebuilds every nested slice
//        object on each update, so even an *unchanged* slice gets a fresh object
//        reference and defeats the provider's per-slice useMemo.
//   Together these produce the re-render cascade HAB-142 targets.
//
//   This store mirrors the provider's session state and lets consumers subscribe
//   to a derived selection guarded by their own equality check (see
//   `useSessionSelector` in context.tsx). It is the same primitive Zustand is
//   built on — chosen over adding a dependency because the repo carries dual
//   lockfiles (package-lock.json + pnpm-lock.yaml) under concurrent edits, and
//   useSyncExternalStore is already the established pattern here (use-auth.ts).

import { DEFAULT_STATE, type AppSessionState } from './types'

type Listener = () => void
type SessionUpdater = (prev: AppSessionState) => AppSessionState

let state: AppSessionState = DEFAULT_STATE
const listeners = new Set<Listener>()

export const sessionStore = {
  /** Current snapshot. Stable reference between writes (required by useSyncExternalStore). */
  getSnapshot(): AppSessionState {
    return state
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },

  /**
   * Replace the snapshot. Accepts a value or a React-style updater. No-ops when
   * the resolved reference is unchanged so identical writes don't notify.
   */
  setState(next: AppSessionState | SessionUpdater): void {
    const resolved = typeof next === 'function' ? (next as SessionUpdater)(state) : next
    if (resolved === state) return
    state = resolved
    listeners.forEach((listener) => listener())
  },

  /** Test-only: restore the initial snapshot and drop any leaked subscribers. */
  resetForTests(): void {
    state = DEFAULT_STATE
    listeners.clear()
  },
}
